import range from 'lodash/range';

export type Squeeze = {
  id: number;
  symbol: string;
};

type calculatePageListArgument = {
  totalPage: number;
  activePage: number;
  maxItemWithoutSqueeze: number;
  itemBeforeSqueeze: number;
  itemAfterSqueeze: number;
  activePageDisplayOffset: number;
  minGapItem: number;
  itemWithoutSqueeze?: number;
};

export default function calculatePageList({
  totalPage,
  activePage,
  maxItemWithoutSqueeze,
  itemBeforeSqueeze,
  itemAfterSqueeze,
  activePageDisplayOffset,
  minGapItem,
  itemWithoutSqueeze,
}: calculatePageListArgument): Array<number | Squeeze> {
  if (itemWithoutSqueeze && itemWithoutSqueeze < totalPage) {
    const itemLeftSide =
      (itemWithoutSqueeze - 1) % 2 !== 0
        ? Math.ceil((itemWithoutSqueeze - 1) / 2)
        : (itemWithoutSqueeze - 1) / 2;
    const itemRightSide =
      (itemWithoutSqueeze - 1) % 2 !== 0
        ? Math.floor((itemWithoutSqueeze - 1) / 2)
        : (itemWithoutSqueeze - 1) / 2;

    let startLimit = activePage - itemLeftSide;
    let endLimit = activePage + itemRightSide;

    while (startLimit < 1) {
      startLimit += 1;
      endLimit += 1;
    }
    while (endLimit > totalPage) {
      endLimit -= 1;
      startLimit -= 1;
    }
    return range(startLimit, endLimit + 1);
  }

  if (totalPage <= maxItemWithoutSqueeze) {
    return range(1, totalPage + 1);
  }

  // page numbers to be displayed before the "..." if squeezing
  const startList = range(1, itemBeforeSqueeze + 1);

  // page numbers to be displayed after the "..." if squeezing
  const endList = range(totalPage - itemAfterSqueeze + 1, totalPage + 1);

  // page numbers to be displayed between "..." if squeezing
  const middleList = range(
    Math.max(1, activePage - activePageDisplayOffset),
    Math.min(totalPage, activePage + activePageDisplayOffset) + 1,
  );

  // if the first page is the current page,
  // insert the next page based on the activePageDisplayOffset
  if (activePage === 1) {
    middleList.push(activePage + activePageDisplayOffset + 1);
  }

  // if the last page is the current page,
  // insert the previous page based on the activePageDisplayOffset
  if (activePage === totalPage) {
    middleList.push(activePage - activePageDisplayOffset - 1);
  }

  const list: Array<number | Squeeze> = Array.from(
    new Set([...startList, ...middleList, ...endList]),
  );

  // sort list as number
  list.sort((a, b) => {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return 0;
  });

  for (let i = 1; i < list.length; i += 1) {
    const item = list[i];
    const prevItem = list[i - 1];

    if (typeof item === 'number' && typeof prevItem === 'number') {
      if (item > prevItem + minGapItem) {
        // insert ... into list with an id
        list.splice(i, 0, { id: item - 1, symbol: '…' });
        i += 1;
      } else if (item > prevItem + 1) {
        // insert missing numbers into list if not squeezing
        list.splice(i, 0, ...range(prevItem + 1, item));
        i += 1;
      }
    }
  }

  return list;
}
